import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Copy, Check, SquareCheck, FileText as FileIcon } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════════
// CHAT MARKDOWN
//
// The renderer for message bodies. Extracted from Chat.js so the `components`
// map is built once per message instead of once per render of the whole page —
// rebuilding it handed react-markdown a new object identity every time any
// unrelated chat state changed, re-rendering every code block and chip.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Rewrite Forge's inline reference syntax into markdown links the renderer
 * below knows how to draw.
 *
 *   @[File: Roadmap.md]  ->  [Roadmap.md](chip://File/Roadmap.md)
 */
export function processContentWithReferences(content, references = []) {
    if (!content) return content;

    let processed = content;

    // 1. Convert @[File: ...] / @[Task: ...] tags to chip links
    processed = processed.replace(/@\[(File|Task): ([^\]]+)\]/g, (match, type, inner) => {
        const cleanContent = inner.trim();
        const safeContent = encodeURIComponent(cleanContent);
        return `[${cleanContent}](chip://${type}/${safeContent})`;
    });

    // 2. Handle legacy references (bare names the model mentioned in prose)
    if (references.length > 0) {
        const sortedRefs = [...references].sort((a, b) => b.name.length - a.name.length);
        sortedRefs.forEach(ref => {
            const escapedName = ref.name.replace(/[.*+?^${ }()|[\]\\]/g, '\\$&');
            const regex = new RegExp(`(?<![\\w-])${escapedName}(?![\\w-])`, 'g');
            // Case-insensitive check because chip links use TitleCase (File)
            // while a ref may be lowercase (file).
            if (!processed.toLowerCase().includes(`](chip://${ref.type.toLowerCase()}/`)) {
                processed = processed.replace(regex, `[${ref.name}](forgeref://${ref.type}/${ref.name})`);
            }
        });
    }

    return processed;
}

/**
 * A fenced code block. Owns its own "copied" flag — the old shared boolean in
 * MessageBubble made every block in a message flash "Copied!" at once.
 */
function CodeBlock({ language, children, codeText }) {
    const [copied, setCopied] = useState(false);

    const copy = () => {
        navigator.clipboard.writeText(codeText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="group relative my-4">
            <div className="overflow-hidden rounded-xl border border-white/10 bg-black/60">
                <div className="flex items-center justify-between border-b border-white/5 bg-black/40 px-3 py-2">
                    <span className="font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                        {language || 'code'}
                    </span>
                    <button
                        onClick={copy}
                        data-testid="copy-code"
                        className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                    >
                        {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                        {copied ? 'Copied!' : 'Copy'}
                    </button>
                </div>
                <pre className="overflow-x-auto p-3">
                    <code className="font-mono text-xs leading-relaxed">{children}</code>
                </pre>
            </div>
        </div>
    );
}

export function ChatMarkdown({ content, isUser = false, references = [], onReferenceClick }) {
    const components = useMemo(() => ({
        // Fenced blocks arrive as <pre><code class="language-x">…</code></pre>;
        // inline code arrives as a bare <code> and never reaches here. That is
        // the only reliable discriminator since react-markdown v9 dropped the
        // `inline` prop — the old `if (!inline)` check was always true, which
        // is why single-word inline code was rendering as a full code card.
        //
        // Returning the card *from* `pre` also replaces the <pre> element
        // outright, so the card's <div> is never nested inside one.
        pre: ({ children }) => {
            const codeEl = React.Children.toArray(children).find(c => c?.props);
            const language = (codeEl?.props?.className || '').replace('language-', '');
            const raw = codeEl?.props?.children;
            const codeText = String(Array.isArray(raw) ? raw.join('') : raw ?? '').replace(/\n$/, '');
            return (
                <CodeBlock language={language} codeText={codeText}>
                    {codeText}
                </CodeBlock>
            );
        },

        code: ({ node, className, children, ...props }) => (
            <code
                className="rounded bg-black/30 px-1.5 py-0.5 font-mono text-[0.85em] text-foreground/90"
                {...props}
            >
                {children}
            </code>
        ),

        // `p` renders as a div because these bodies contain block-level cards.
        // Anything styling paragraphs must target this, not `p`.
        p: ({ children }) => <div className="mb-4 last:mb-0">{children}</div>,
        ul: ({ children }) => <ul className="mb-4 list-outside list-disc space-y-1.5 pl-5 last:mb-0">{children}</ul>,
        ol: ({ children }) => <ol className="mb-4 list-outside list-decimal space-y-1.5 pl-5 last:mb-0">{children}</ol>,
        li: ({ children }) => <li className="pl-1 marker:text-muted-foreground">{children}</li>,
        h1: ({ children }) => <h1 className="mb-3 mt-6 font-mono text-xl font-bold tracking-tight first:mt-0">{children}</h1>,
        h2: ({ children }) => <h2 className="mb-2.5 mt-6 font-mono text-lg font-bold tracking-tight first:mt-0">{children}</h2>,
        h3: ({ children }) => <h3 className="mb-2 mt-5 font-mono text-base font-bold tracking-tight first:mt-0">{children}</h3>,
        hr: () => <hr className="my-6 border-white/10" />,
        strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
        table: ({ children }) => (
            <div className="my-4 overflow-x-auto rounded-xl border border-white/10">
                <table className="w-full border-collapse text-sm">{children}</table>
            </div>
        ),
        th: ({ children }) => (
            <th className="border-b border-white/10 bg-white/5 px-3 py-2 text-left font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                {children}
            </th>
        ),
        td: ({ children }) => <td className="border-b border-white/5 px-3 py-2 align-top">{children}</td>,

        a: ({ href, children }) => {
            // Inline reference pill (non-interactive, echoes what the user typed)
            if (href?.startsWith('chip://')) {
                const [, type, inner] = href.match(/chip:\/\/([^/]+)\/(.+)/) || [];
                const decodedContent = inner ? decodeURIComponent(inner) : children;
                const isFile = type === 'File';

                return (
                    <span
                        className={`mx-1 inline-flex translate-y-[1px] items-center gap-1 rounded-md border px-2 py-0.5 align-middle text-xs font-medium ${
                            isUser
                                ? 'border-white/20 bg-white/20 text-white'
                                : isFile
                                    ? 'border-purple-500/20 bg-purple-500/10 text-purple-300'
                                    : 'border-green-500/20 bg-green-500/10 text-green-300'
                        }`}
                    >
                        {isFile ? <FileIcon className="h-3 w-3 opacity-70" /> : <SquareCheck className="h-3 w-3 opacity-70" />}
                        <span className="max-w-[150px] truncate pb-[1px] leading-none">{decodedContent}</span>
                    </span>
                );
            }

            // Clickable reference the model resolved on its own
            if (href?.startsWith('forgeref://')) {
                const parts = href.replace('forgeref://', '').split('/');
                const type = parts[0];
                const name = parts.slice(1).join('/');
                const isTask = type === 'Task';
                return (
                    <button
                        onClick={() => onReferenceClick?.(type, name)}
                        data-testid={`chat-reference-${type}`}
                        className={`mx-1 my-0.5 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold shadow-sm transition-all duration-200 hover:scale-[1.02] hover:shadow-md active:scale-[0.98] ${
                            isTask
                                ? 'border border-green-500/40 bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-300 ring-1 ring-green-500/10 hover:from-green-500/30 hover:to-emerald-500/30'
                                : 'border border-blue-500/40 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-300 ring-1 ring-blue-500/10 hover:from-blue-500/30 hover:to-cyan-500/30'
                        }`}
                    >
                        {isTask ? <SquareCheck className="h-3.5 w-3.5" /> : <FileIcon className="h-3.5 w-3.5" />}
                        <span>{children}</span>
                    </button>
                );
            }

            return (
                <a href={href} className="text-primary underline hover:no-underline" target="_blank" rel="noopener noreferrer">
                    {children}
                </a>
            );
        },

        blockquote: ({ children }) => {
            // A quoted editor selection arrives as `> [Referenced file.js:1-10]`
            // followed by the code. Render those as a source card instead.
            let textContent = '';
            React.Children.forEach(children, child => {
                if (typeof child === 'string') textContent += child;
                else if (child?.props?.children) {
                    if (typeof child.props.children === 'string') textContent += child.props.children;
                    else if (Array.isArray(child.props.children)) {
                        child.props.children.forEach(c => {
                            if (typeof c === 'string') textContent += c;
                        });
                    }
                }
            });

            const match = textContent.match(/\[Referenced ([^\]]+)\]\s*([\s\S]*)/);

            if (match) {
                const [, meta, quoted] = match;
                return (
                    <div className="my-3 overflow-hidden rounded-xl border border-white/10 bg-black/20">
                        <div className="flex select-none items-center gap-2 border-b border-white/5 bg-white/5 px-3 py-1.5 text-xs text-muted-foreground">
                            <FileIcon className="h-3 w-3" />
                            <span className="truncate font-mono text-[10px] font-bold uppercase tracking-[0.16em]">{meta}</span>
                        </div>
                        <div className="overflow-x-auto whitespace-pre-wrap p-3 font-mono text-xs leading-relaxed text-white/70">
                            {quoted}
                        </div>
                    </div>
                );
            }

            return (
                <blockquote className="mb-4 border-l-2 border-primary/50 pl-3 italic text-muted-foreground last:mb-0">
                    {children}
                </blockquote>
            );
        },
    }), [isUser, onReferenceClick]);

    const processed = useMemo(
        () => processContentWithReferences(content, references),
        [content, references]
    );

    return (
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            // Default urlTransform strips unknown protocols — chip:// and
            // forgeref:// must survive for the handlers above to ever run.
            urlTransform={(url) => url}
            components={components}
        >
            {processed}
        </ReactMarkdown>
    );
}

export default ChatMarkdown;
